import { useCallback, useContext, useEffect, useRef } from 'react';
import Axios, { type CancelTokenSource } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useDebounceValue } from '@trinity/chat-components';
import type { WorkspaceGlobalSearchBarFilters } from '@/types';
import {
  WorkspaceGlobalSearchBarDispatchContext, WorkspaceGlobalSearchBarQueryContext,
  WorkspaceGlobalSearchBarFetchContext, WorkspaceGlobalSearchBarFiltersContext,
} from './Context';

export default function Fetch({ children }: ChildrenProps) {
  const [dispatch] = useContext(WorkspaceGlobalSearchBarDispatchContext);
  const { query } = useContext(WorkspaceGlobalSearchBarQueryContext);
  const { activeFilters, setAggregationsFilters } = useContext(WorkspaceGlobalSearchBarFiltersContext);
  const cancelSourceRef = useRef<CancelTokenSource>(undefined);

  const debouncedValue = useDebounceValue<string>(query, 100);

  const apiClient = useChatApiClient();

  const fetch = useCallback(async (query: string, filters?: WorkspaceGlobalSearchBarFilters) => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel();
    }

    cancelSourceRef.current = Axios.CancelToken.source();

    const result = await apiClient.workspacesGlobalSearch.bar({
      query,
      filters,
      cancelToken: cancelSourceRef.current.token,
    });

    if (result) {
      dispatch({ items: result.data.items });
      setAggregationsFilters(result.data.aggregations);
    }
  }, [dispatch, setAggregationsFilters]);

  useEffect(() => {
    if (!debouncedValue.length) return;
    fetch(debouncedValue, activeFilters);
  }, [debouncedValue, activeFilters, fetch]);

  return (
    <WorkspaceGlobalSearchBarFetchContext.Provider value={{ fetch }}>
      {children}
    </WorkspaceGlobalSearchBarFetchContext.Provider>
  );
}

export { Fetch };